import behaviourManagementConstants from "@/constants/behaviourManagementConstants.js"

/**
 * School-configurable behaviour product copy. Defaults match the current English
 * and Spanish UI. A single API response can send a **partial** object of the same
 * camelCase keys; only provided non-empty strings override defaults for the active school.
 *
 * @typedef {Object} BehaviourTerminologyApiPayload
 * @property {string} [behaviourManagement] Modules page module title (e.g. "Behaviour Management")
 * @property {string} [behaviourManagementSettings] Sidebar / settings page title
 * @property {string} [coreValue] Singular label (e.g. tab "Core value")
 * @property {string} [coreValuesLower] Lower plural phrase used mid-sentence (e.g. "core values")
 * @property {string} [positiveBehaviours] Positive behaviours tab label
 * @property {string} [point] Singular "point" word
 * @property {string} [points] Plural "points" (e.g. column header)
 * @property {string} [automatedPointsTab] Automated points tab label
 * @property {string} [customizedThemeTab] Customized theme tab label
 * @property {string} [tabsAriaLabel] Tab list accessible description
 * @property {string} [coreValueSectionTitle] Core value settings screen title
 * @property {string} [emptyTabTitle] Placeholder tab heading
 * @property {string} [emptyTabBody] Placeholder tab body
 * @property {string} [addCoreValue] Add button label
 * @property {string} [noRows] Empty table message
 * @property {string} [colPoints] Points column header (usually same as `points`)
 * @property {string} [announceLoadedPaginated] Supports `{count}`, `{total}`
 * @property {string} [announceLoadedLazy] Supports `{count}`, `{total}`
 * @property {string} [announceLazyMore] Supports `{visible}`, `{total}`
 * @property {string} [positiveBehavioursSectionTitle]
 * @property {string} [addPositiveBehaviour]
 * @property {string} [noPositiveBehavioursRows]
 * @property {string} [colAwardDescription]
 * @property {string} [positiveBehavioursAnnounceLoadedPaginated]
 * @property {string} [positiveBehavioursAnnounceLoadedLazy]
 * @property {string} [positiveBehavioursAnnounceLazyMore]
 * @property {string} [automatedPointsSectionTitle]
 * @property {string} [pointsAwardedLabel]
 * @property {string} [onTimeForSchool]
 * @property {string} [onTimeForClass]
 * @property {string} [onTimeForSchoolDescription]
 * @property {string} [onTimeForClassDescription]
 * @property {string} [customizedThemeSectionTitle]
 * @property {string} [renameLabelsTitle]
 * @property {string} [customizedThemeDescription]
 * @property {string} [moduleDescription] Modules page long description under toggle
 * @property {string} [pointHistoryTab] Point history tab label
 * @property {string} [myPointHistoryTab] My point history tab label
 * @property {string} [schoolWidePointHistoryTab] School-wide point history tab label
 * @property {string} [studentListTab] Student list tab label
 * @property {string} [uploadLogTab] Upload log tab label
 * @property {string} [manageTabsAriaLabel] Manage section tab list aria label
 * @property {string} [statSchoolWidePointsAwarded]
 * @property {string} [statSchoolWidePointsRedeemed]
 * @property {string} [statPointsYouAwarded]
 * @property {string} [statPointsRedeemed]
 * @property {string} [statTotalPointsEarned]
 * @property {string} [statPointsUsedOnRewards]
 * @property {string} [statRemainingPoints]
 */

const MODULE_DESCRIPTION_EN = behaviourManagementConstants.MODULE_DESCRIPTION

const MODULE_DESCRIPTION_ES =
  "Permite a docentes y administradores fomentar conductas positivas de los estudiantes mediante un sistema de recompensas por puntos al estilo PBIS."

/**
 * @param {string} template
 * @param {Record<string, string | number>} vars
 * @returns {string}
 */
export function formatBehaviourTerminologyTemplate(template, vars) {
  if (template == null) return ""
  return String(template).replace(/\{(\w+)\}/g, (_, key) => {
    const v = vars?.[key]
    return v != null ? String(v) : ""
  })
}

const EN = Object.freeze({
  behaviourManagement: "Behavior Management",
  behaviourManagementSettings: "Behavior Management Settings",
  coreValue: "Core value",
  coreValuesLower: "core values",
  positiveBehaviours: "Positive behaviors",
  point: "Point",
  points: "Points",
  automatedPointsTab: "Automated {points}",
  customizedThemeTab: "Customized theme",
  tabsAriaLabel: "Behavior management settings sections",
  coreValueSectionTitle: "Core value settings",
  emptyTabTitle: "Behavior settings",
  emptyTabBody: "This section will be available in a future update.",
  addCoreValue: "Add core value",
  noRows: "No core values match the current filters.",
  colPoints: "{points}",
  announceLoadedPaginated:
    "Showing {count} of {total} core values on this page.",
  announceLoadedLazy:
    "Loaded {count} of {total} core values. Scroll the table to load more.",
  announceLazyMore: "Loaded {visible} of {total} core values into the table.",
  positiveBehavioursSectionTitle: "Positive behaviors settings",
  addPositiveBehaviour: "Add positive behavior",
  noPositiveBehavioursRows: "No positive behaviors match the current filters.",
  colAwardDescription: "Award description",
  positiveBehavioursAnnounceLoadedPaginated:
    "Showing {count} of {total} positive behaviors on this page.",
  positiveBehavioursAnnounceLoadedLazy:
    "Loaded {count} of {total} positive behaviors. Scroll the table to load more.",
  positiveBehavioursAnnounceLazyMore:
    "Loaded {visible} of {total} positive behaviors into the table.",
  automatedPointsSectionTitle: "Automated {points} settings",
  pointsAwardedLabel: "{points} awarded",
  onTimeForSchool: "On time for school",
  onTimeForClass: "On time for class",
  onTimeForSchoolDescription:
    "Automatically reward students for arriving on time. Students with zero LTS (Late to School) passes will earn the specified points at the end of the selected frequency.",
  onTimeForClassDescription:
    "Automatically reward students for arriving on time. Students with zero LTC (Late to Class) passes will earn the specified points at the end of the selected frequency.",
  customizedThemeSectionTitle: "Customized theme settings",
  renameLabelsTitle: "Rename labels",
  customizedThemeDescription:
    "Customize how Behaviour Management terms appear across the module for all users. When rename is off, default labels are used. Each custom label supports up to 25 alphanumeric characters.",
  moduleDescription: MODULE_DESCRIPTION_EN,
  pointHistoryTab: "{point} history",
  myPointHistoryTab: "My {point} history",
  schoolWidePointHistoryTab: "School-wide {point} history",
  studentListTab: "Student list",
  uploadLogTab: "Upload log",
  manageTabsAriaLabel: "Behaviour management sections",
  statSchoolWidePointsAwarded: "School-wide {points} awarded",
  statSchoolWidePointsRedeemed: "School-wide {points} redeemed",
  statPointsYouAwarded: "{points} you awarded",
  statPointsRedeemed: "{points} redeemed",
  statTotalPointsEarned: "Total {points} earned",
  statPointsUsedOnRewards: "{points} used on rewards",
  statRemainingPoints: "Remaining {points}"
})

const ES = Object.freeze({
  behaviourManagement: "Gestión de Conducta",
  behaviourManagementSettings: "Configuración de Gestión de Conducta",
  coreValue: "Valor central",
  coreValuesLower: "valores centrales",
  positiveBehaviours: "Conductas positivas",
  point: "Punto",
  points: "Puntos",
  automatedPointsTab: "{points} automatizados",
  customizedThemeTab: "Tema personalizado",
  tabsAriaLabel: "Secciones de configuración de gestión de conducta",
  coreValueSectionTitle: "Configuración de valores centrales",
  emptyTabTitle: "Configuración de conducta",
  emptyTabBody: "Esta sección estará disponible en una actualización futura.",
  addCoreValue: "Agregar valor central",
  noRows: "Ningún valor central coincide con los filtros actuales.",
  colPoints: "{points}",
  announceLoadedPaginated:
    "Mostrando {count} de {total} valores centrales en esta página.",
  announceLoadedLazy:
    "Cargados {count} de {total} valores centrales. Desplácese para cargar más.",
  announceLazyMore:
    "Cargados {visible} de {total} valores centrales en la tabla.",
  positiveBehavioursSectionTitle: "Configuración de conductas positivas",
  addPositiveBehaviour: "Agregar conducta positiva",
  noPositiveBehavioursRows:
    "Ninguna conducta positiva coincide con los filtros actuales.",
  colAwardDescription: "Descripción del reconocimiento",
  positiveBehavioursAnnounceLoadedPaginated:
    "Mostrando {count} de {total} conductas positivas en esta página.",
  positiveBehavioursAnnounceLoadedLazy:
    "Cargados {count} de {total} conductas positivas. Desplácese para cargar más.",
  positiveBehavioursAnnounceLazyMore:
    "Cargados {visible} de {total} conductas positivas en la tabla.",
  automatedPointsSectionTitle: "Configuración de {points} automatizados",
  pointsAwardedLabel: "{points} otorgados",
  onTimeForSchool: "A tiempo para la escuela",
  onTimeForClass: "A tiempo para la clase",
  onTimeForSchoolDescription:
    "Recompensa automáticamente a los estudiantes por llegar a tiempo. Los estudiantes con cero pases LTS (tarde a la escuela) ganarán los puntos indicados al final de la frecuencia seleccionada.",
  onTimeForClassDescription:
    "Recompensa automáticamente a los estudiantes por llegar a tiempo. Los estudiantes con cero pases LTC (tarde a clase) ganarán los puntos indicados al final de la frecuencia seleccionada.",
  customizedThemeSectionTitle: "Configuración de tema personalizado",
  renameLabelsTitle: "Renombrar etiquetas",
  customizedThemeDescription:
    "Personalice cómo aparecen los términos de Gestión de Conducta en el módulo para todos los usuarios. Si el cambio de nombre está desactivado, se usan las etiquetas predeterminadas. Cada etiqueta admite hasta 25 caracteres alfanuméricos.",
  moduleDescription: MODULE_DESCRIPTION_ES,
  pointHistoryTab: "Historial de {points}",
  myPointHistoryTab: "Mi historial de {points}",
  schoolWidePointHistoryTab: "Historial escolar de {points}",
  studentListTab: "Lista de estudiantes",
  uploadLogTab: "Registro de cargas",
  manageTabsAriaLabel: "Secciones de gestión de conducta",
  statSchoolWidePointsAwarded: "{points} otorgados en la escuela",
  statSchoolWidePointsRedeemed: "{points} canjeados en la escuela",
  statPointsYouAwarded: "{points} que otorgó",
  statPointsRedeemed: "{points} canjeados",
  statTotalPointsEarned: "Total de {points} ganados",
  statPointsUsedOnRewards: "{points} usados en recompensas",
  statRemainingPoints: "{points} restantes"
})

/** @type {Readonly<Record<'en' | 'es', Readonly<BehaviourTerminologyApiPayload>>>} */
export const BEHAVIOUR_TERMINOLOGY_LOCALE_DEFAULTS = Object.freeze({
  en: EN,
  es: ES
})

/** Keys schools may return in one terminology API object (subset allowed). */
export const BEHAVIOUR_TERMINOLOGY_KEYS = Object.freeze(Object.keys(EN))

/** Keys re-rendered from locale templates when `point` / `points` are customized. */
export const POINTS_DERIVED_TERMINOLOGY_KEYS = Object.freeze([
  "pointHistoryTab",
  "myPointHistoryTab",
  "schoolWidePointHistoryTab",
  "statSchoolWidePointsAwarded",
  "statSchoolWidePointsRedeemed",
  "statPointsYouAwarded",
  "statPointsRedeemed",
  "statTotalPointsEarned",
  "statPointsUsedOnRewards",
  "statRemainingPoints",
  "pointsAwardedLabel",
  "automatedPointsTab",
  "automatedPointsSectionTitle",
  "colPoints"
])

/**
 * @param {Record<string, string>} merged
 * @param {Readonly<Record<string, string>>} baseTemplates
 * @returns {Record<string, string>}
 */
export function applyPointsDerivedLabels(merged, baseTemplates) {
  const vars = {
    point: merged.point ?? baseTemplates.point ?? "",
    points: merged.points ?? baseTemplates.points ?? "",
    behaviourManagement:
      merged.behaviourManagement ?? baseTemplates.behaviourManagement ?? "",
    coreValue: merged.coreValue ?? baseTemplates.coreValue ?? "",
    positiveBehaviours:
      merged.positiveBehaviours ?? baseTemplates.positiveBehaviours ?? ""
  }
  const out = { ...merged }
  for (const key of POINTS_DERIVED_TERMINOLOGY_KEYS) {
    const template = baseTemplates[key]
    if (template) {
      out[key] = formatBehaviourTerminologyTemplate(template, vars)
    }
  }
  return out
}

/**
 * @param {Readonly<Record<string, string>>} base
 * @param {BehaviourTerminologyApiPayload | null | undefined} apiPayload
 * @returns {Record<string, string>}
 */
export function mergeBehaviourTerminologyFromApi(base, apiPayload) {
  const out = { ...base }
  if (!apiPayload || typeof apiPayload !== "object") {
    return out
  }
  for (const key of BEHAVIOUR_TERMINOLOGY_KEYS) {
    const v = apiPayload[key]
    if (v != null && String(v).trim() !== "") {
      out[key] = String(v).trim()
    }
  }
  return out
}

/**
 * @param {string} [locale]
 * @param {BehaviourTerminologyApiPayload | null | undefined} apiPayload
 * @returns {Record<string, string>}
 */
export function resolveBehaviourTerminology(locale, apiPayload) {
  const normalized = String(locale || "en")
    .toLowerCase()
    .startsWith("es")
    ? "es"
    : "en"
  const base =
    BEHAVIOUR_TERMINOLOGY_LOCALE_DEFAULTS[normalized] ||
    BEHAVIOUR_TERMINOLOGY_LOCALE_DEFAULTS.en
  const merged = mergeBehaviourTerminologyFromApi(base, apiPayload)
  return applyPointsDerivedLabels(merged, base)
}
